/**
 * Live events on a time grid, one row per stage (Agenda's schedule, Club TV's fixtures, a church's
 * services): what is on when and where, sessions that clash on one stage, and the now line.
 * Read-only: a session is edited in its own editor, opened from its block.
 */

import { useEffect, useMemo, useState } from "react";
import Chip from "@web-ui/components/Chip";
import Empty from "@web-ui/components/Empty";
import Tabs from "@web-ui/components/Tabs";
import { entities, type Entity } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { href, itemRoute } from "@/lib/route";

const HOUR = 3_600_000;
const DEFAULT_LENGTH = 3 * HOUR;

interface Block { id: string; label: string; start: number; end: number; stage: string; status: string; clash: boolean }

function dayKey(ms: number) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function ScheduleGrid({ client }: { client: AdminClient }) {
  const [events, setEvents] = useState<Entity[] | null>(null);
  const [stages, setStages] = useState<Entity[]>([]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    void Promise.all([entities.listFull(client, "liveEvent"), entities.listFull(client, "stage")]).then(([e, s]) => { setEvents(e.entities); setStages(s.entities); });
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [client]);

  const blocks = useMemo<Block[]>(() => {
    const list = (events ?? []).filter((e) => e.status !== "archived").map((e) => {
      const d = e.data as { startsAt: string; endsAt?: string; stageId?: string };
      const start = Date.parse(d.startsAt);
      return { id: e.id, label: e.label, start, end: d.endsAt ? Date.parse(d.endsAt) : start + DEFAULT_LENGTH, stage: d.stageId ?? "", status: e.status, clash: false };
    }).sort((a, b) => a.start - b.start);
    for (const a of list) for (const b of list) if (a !== b && a.stage === b.stage && a.stage && a.start < b.end && b.start < a.end) a.clash = true;
    return list;
  }, [events]);

  const days = [...new Set(blocks.map((b) => dayKey(b.start)))];
  const [day, setDay] = useState<string | null>(null);
  const shownDay = day && days.includes(day) ? day : days.find((d) => d >= dayKey(now)) ?? days[0];
  const dayBlocks = blocks.filter((b) => dayKey(b.start) === shownDay);

  if (events === null) return <div className="card" aria-busy="true">Loading the schedule…</div>;
  if (!blocks.length) return <div className="card"><Empty icon="calendar-1" title="Nothing scheduled" description="Live items appear here by their start time and stage." /></div>;

  const from = Math.floor(Math.min(...dayBlocks.map((b) => b.start)) / HOUR) * HOUR;
  const to = Math.ceil(Math.max(...dayBlocks.map((b) => b.end)) / HOUR) * HOUR;
  const span = Math.max(HOUR, to - from);
  const pos = (t: number) => `${((t - from) / span) * 100}%`;
  const rows = [
    ...stages.map((s) => ({ id: s.id, label: s.label })),
    ...(dayBlocks.some((b) => !b.stage || !stages.some((s) => s.id === b.stage)) ? [{ id: "", label: "No stage" }] : []),
  ];
  const hours = Array.from({ length: Math.round(span / HOUR) + 1 }, (_, i) => from + i * HOUR);
  const time = (t: number) => new Date(t).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <section className="card flex flex-col gap-4">
      {days.length > 1 ? (
        <Tabs items={days.map((d, id) => ({ id, name: new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) }))}
          value={{ id: days.indexOf(shownDay!), name: "" }} setValue={(o) => setDay(days[o.id]!)} />
      ) : null}
      <div className="overflow-x-auto">
        <div className="relative min-w-[48rem]">
          <div className="relative ml-36 h-6">
            {hours.map((h) => <span key={h} className="absolute -translate-x-1/2 text-caption text-t-tertiary" style={{ left: pos(h) }}>{time(h)}</span>)}
          </div>
          {rows.map((row) => (
            <div key={row.id || "none"} className="flex items-stretch border-t border-s-stroke2">
              <div className="w-36 shrink-0 py-3 pr-3 text-body-2">{row.label}</div>
              <div className="relative h-20 grow">
                {dayBlocks.filter((b) => (row.id ? b.stage === row.id : !b.stage || !stages.some((s) => s.id === b.stage))).map((b) => (
                  <a key={b.id} href={href(itemRoute(client.brand.id, "liveEvent", b.id))}
                    className={`absolute top-2 bottom-2 overflow-hidden rounded-xl px-2 py-1 text-caption ${b.clash ? "bg-accent-container text-on-accent-container outline-2 outline-accent" : "bg-accent-container text-on-accent-container hover:outline-2 hover:outline-accent-stroke"}`}
                    style={{ left: pos(b.start), width: `calc(${((b.end - b.start) / span) * 100}% - 2px)` }}
                    title={`${b.label}: ${time(b.start)}–${time(b.end)}${b.clash ? " (clashes with another session on this stage)" : ""}`}>
                    <span className="block truncate text-body-2">{b.label}</span>
                    <span className="block truncate opacity-80">{time(b.start)}–{time(b.end)}{b.status === "draft" ? " · Draft" : ""}</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
          {now >= from && now <= from + span ? <span className="pointer-events-none absolute top-6 bottom-0 ml-36 w-0.5 bg-accent" style={{ left: `calc((100% - 9rem) * ${(now - from) / span})` }} aria-label="Now" /> : null}
        </div>
      </div>
      {dayBlocks.some((b) => b.clash) ? <p className="text-caption"><Chip size="sm" tone="warning">clash</Chip> Outlined items overlap another on the same stage.</p> : null}
      <p className="text-caption text-t-tertiary">Times in your own time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).</p>
    </section>
  );
}
